import posthog from "posthog-js";

import { isRTKError } from "utils/errors/isRTKError";

import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import type { CaptureOptions } from "posthog-js";

import type { ObjectValuesAsType } from "utils/types";

import type { POSTHOG_EVENTS } from "./events";

type PosthogEvent = ObjectValuesAsType<typeof POSTHOG_EVENTS>;

export interface CaptureEventProperties {
  [key: string]: unknown;
  apiResponse?: unknown;
  entityId?: string;
  errorCode?: number | string;
  errorMessage?: string;
  errorName?: string;
  flow?: string;
  statusCode?: number | string;
}

export function captureEvent(
  event: PosthogEvent,
  properties?: CaptureEventProperties,
  options?: CaptureOptions
) {
  try {
    posthog.capture(event, properties, options);
  } catch {
    // ignore analytics errors
  }
}

export function extractErrorProperties(error: unknown): CaptureEventProperties {
  if (!error) {
    return {};
  }

  if (isRTKError(error as FetchBaseQueryError | SerializedError)) {
    const rtkError = error as FetchBaseQueryError & {
      data: { exception: { code: number; message: string } };
    };

    return {
      statusCode: rtkError.status,
      errorCode: rtkError.data.exception.code,
      errorMessage: rtkError.data.exception.message,
      apiResponse: rtkError.data,
    };
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    "data" in error
  ) {
    const baseQueryError = error as FetchBaseQueryError;
    const data = baseQueryError.data as any;

    return {
      statusCode: baseQueryError.status,
      errorCode: data?.exception?.code,
      errorMessage: data?.exception?.message,
      apiResponse: data,
    };
  }

  if (error instanceof Error) {
    return {
      errorName: error.name,
      errorMessage: error.message,
    };
  }

  return {};
}
